import { FC } from "react"
import { X } from "lucide-react"

export interface FilterChipData {
  period?: string
  viewType?: "daily" | "monthly"
  category: string
  status?: "success" | "failed" | "active" | "disabled" | "deleted"
  index: number
}

interface FilterChipsProps {
  filter: FilterChipData | null
  onClear: () => void
  currentCount: number
  totalCount: number
  itemLabel?: string
}

const FilterChips: FC<FilterChipsProps> = ({
  filter,
  onClear,
  currentCount,
  totalCount,
  itemLabel = "items"
}) => {
  if (!filter) return null

  const getStatusConfig = (status?: string) => {
    switch (status) {
      case "success":
        return {
          bgColor: "bg-green-100",
          textColor: "text-green-800",
          hoverColor: "hover:bg-green-200",
          label: "Successful"
        }
      case "failed":
        return {
          bgColor: "bg-red-100",
          textColor: "text-red-800",
          hoverColor: "hover:bg-red-200",
          label: "Failed"
        }
      case "active":
        return {
          bgColor: "bg-purple-100",
          textColor: "text-purple-800",
          hoverColor: "hover:bg-purple-200",
          label: "Active"
        }
      case "disabled":
        return {
          bgColor: "bg-yellow-100",
          textColor: "text-yellow-800",
          hoverColor: "hover:bg-yellow-200",
          label: "Disabled"
        }
      case "deleted":
        return {
          bgColor: "bg-red-100",
          textColor: "text-red-800",
          hoverColor: "hover:bg-red-200",
          label: "Deleted"
        }
      default:
        return {
          bgColor: "bg-gray-100",
          textColor: "text-gray-800",
          hoverColor: "hover:bg-gray-200",
          label: status || ""
        }
    }
  }

  const statusConfig = filter.status ? getStatusConfig(filter.status) : null

  return (
    <div className="bg-white px-6 py-3 border-t border-b border-gray-200 flex items-center gap-3 flex-wrap">
      <span className="text-sm text-gray-600 font-medium">Active Filters:</span>

      {/* Date Filter Chip */}
      <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
        <span>{filter.category}</span>
        <button
          onClick={onClear}
          className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
          title="Clear filter"
        >
          <X size={14} />
        </button>
      </div>

      {/* Status Filter Chip */}
      {statusConfig && (
        <div
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${statusConfig.bgColor} ${statusConfig.textColor}`}
        >
          <span>{statusConfig.label}</span>
          <button
            onClick={onClear}
            className={`rounded-full p-0.5 transition-colors ${statusConfig.hoverColor}`}
            title="Clear filter"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Result Count Badge */}
      <div className="ml-auto text-sm text-gray-600">
        Showing <span className="font-semibold">{currentCount}</span> of{" "}
        <span className="font-semibold">{totalCount}</span> {itemLabel}
      </div>
    </div>
  )
}

export default FilterChips
